import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES, CATEGORY_TYPES } from 'shared/constants';
import styles from './lab-category.module.scss';
import classnames from 'classnames';
import _ from 'lodash';
import { useDrag, useDrop } from 'react-dnd';
import { Lang } from 'shared/constants/lang';
import { CategoryBoxComponent } from 'src/components/common/categories/category-box';
import { useViewSelector } from 'src/components/common/view-selector';
import { CategoryRowComponent } from 'src/components/common/categories/category-row';
import { useSelector } from 'react-redux';
import { haveEditPermissions } from 'shared/store/selectors/knowledge.selector';
import { ActionsDropdown } from 'src/components/common/actions-dropdown/actions-dropdown.component';
import { LabCategoryMovePopup } from './lab-category-move/lab-category-move';

const LabCategory = React.memo(function LabCategory({
  item = {
    title: '',
    id: '',
  },
  labSelectValues = () => {},
  activeId = null,
  adminMode = false,
  deleteCategory = () => {},
  updateCategory = () => {},
  dragItem,
  findItem = () => {},
  finishDrag = () => {},
  dndGroup,
  editCategory = () => {},
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
}) {
  let { id, title, type, ownerIds, tags, drugClass, path, values } = item;
  const [isMoveOpen, setMoveOpen] = React.useState(false);
  const navigate = useNavigate();
  const isLabCategory = type === CATEGORY_TYPES.VALUES;
  let focusStyle = !_.isNull(activeId) ? (activeId === id ? styles.active : styles.inActive) : null;

  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  const originalIndex = findItem(id)?.index;

  const [{ isDragging }, drag] = useDrag({
    item: { type: dndGroup, id, originalIndex },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => adminMode,
    end: (dropResult, monitor) => {
      const { id: droppedId, originalIndex } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        dragItem(droppedId, originalIndex);
      }

      finishDrag(droppedId);
    },
  });
  const [, drop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId }) {
      if (draggedId !== id) {
        const { index: overIndex } = findItem(id);
        dragItem(draggedId, overIndex);
      }
    },
  });

  const onClick = React.useCallback(() => {
    if (isLabCategory) {
      labSelectValues(id);
    } else {
      navigate(ROUTES.LABS_CATEGORIES.replace(':id', id));
    }
  }, [navigate, id, isLabCategory, labSelectValues]);

  const editCategoryAction = React.useCallback(
    e => {
      // TODO: analytics
      if (!hasEditPermission) {
        return;
      }
      editCategory({ id, title, ownerIds, tags, drugClass, path, type });
    },
    [hasEditPermission, editCategory, id, title, ownerIds, tags, drugClass, path, type],
  );

  const onBlur = React.useCallback(
    e => {
      const { textContent: value } = e.target;
      // TODO: analytics
      if (value === title) return false;
      updateCategory({ id, title: value, ownerIds, tags });
    },
    [id, title, ownerIds, tags, updateCategory],
  );

  const deleteCategoryAction = React.useCallback(
    e => {
      // TODO: analytics
      if (!hasEditPermission) {
        return;
      }
      deleteCategory({ id, title, path, type, ownerIds });
    },
    [hasEditPermission, deleteCategory, id, title, path, type, ownerIds],
  );

  const moveCategoryAction = React.useCallback(
    e => {
      if (!hasEditPermission) {
        return;
      }

      setMoveOpen(true);
    },
    [hasEditPermission],
  );

  const adminActions = [
    {
      label: lang.EDIT,
      onClick: editCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.MOVE,
      onClick: moveCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
    {
      label: lang.DELETE,
      onClick: deleteCategoryAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission,
    },
  ];

  const opacity = isDragging ? 0 : 1;

  const { isGridView } = useViewSelector();
  const CategoryComponent = isGridView ? CategoryBoxComponent : CategoryRowComponent;
  const displayItemsCount = values?.length || 0;

  return (
    <div
      ref={node => drag(drop(node))}
      style={{ opacity }}
      className={classnames(
        isGridView ? styles.box : {},
        focusStyle,
        adminMode ? styles.admin : {},
      )}>
      <CategoryComponent
        title={title}
        tags={tags}
        adminMode={adminMode}
        hasEditPermission={hasEditPermission}
        type={type}
        onClick={onClick}
        onBlur={onBlur}
        actions={adminMode && <ActionsDropdown actions={adminActions} />}
        categoryType={CATEGORY_TYPES.VALUES}
        itemsCount={displayItemsCount}
        metadata={{ items: displayItemsCount }}
        renderItemOptions={{
          showTags: true,
          showMetadata: true,
        }}
      />
      <LabCategoryMovePopup
        isOpen={isMoveOpen}
        categoryId={id}
        id={id}
        onFinish={() => setMoveOpen(false)}
      />
    </div>
  );
});

export { LabCategory };
